import { API_ROUTES, postEncryptedJson, postEncryptedFormData, updateProfile, updateProfileStatus } from "../utils/apiClient";
import AppConstants from "../utils/appConstants";

function buildDocImageUrl(filePath) {
  if (!filePath) return filePath;
  return filePath;
}

const UserDocumentType = {
  PROOF_OF_IDENTITY: 0,
  PROOF_OF_ADDRESS: 1,
  PROFILE: 2,
  SELFIE: 3,
  OTHER: 4,
  PROOF_OF_SIGNATURE: 8,
  PROOF_OF_VIDEO_SIGNATURE: 14,
};

const UserDocumentSubType = {
  OTHER: 0,
  ID_CARD: 1,
  PASSPORT: 2,
  CARD_OF_RESIDENCE: 3,
  PALESTINIAN_CARD: 4,
  VISA: 5,
  SELFIE: 7,
  UTILITY_BILL: 10,
  PROFILE: 11,
  CERTIFICATE_OF_RESIDENCE: 12,
  SIGNATURE: 13,
  CHECK_SIGNATURE: 15,
  SIGNATURE_VIDEO: 16,
};

const DocumentFileName = {
  FRONT_IMAGE: "FRONT_ID_IMAGE",
  BACK_IMAGE: "BACK_ID_IMAGE",
  ADDRESS_IMAGE: "ADDRESS_IMAGE",
  SELFIE: "SELFIE_IMAGE",
  PROFILE: "PROFILE_IMAGE",
  OTHER: "OTHER",
  SIGNATURE_IMAGE: "SIGNATURE_IMAGE",
  SIGNATURE_VIDEO: "SIGNATURE_VIDEO",
  COMPANY_IMAGE: "COMPANY_IMAGE",
};

const KycStatus = {
  UPDATED_KYC: -3,
  HAS_KYC_ERROR: -2,
  HAS_APPROVAL_ERROR: -1,
  PENDING: 0,
  APPROVED: 1,
  VERIFIED: 2,
  VERIFIED_NOT_CHECKED: 3,
};

export { UserDocumentType, UserDocumentSubType, DocumentFileName, KycStatus };

class ProfileInfoController {
  static profileInfoCache = new Map();
  static profileInfoInFlight = new Map();
  static PROFILE_INFO_CACHE_TTL_MS = 10 * 60 * 1000;

  /**
   * Fetch full profile data (with documents).
   */
  static async getProfileInfo({ withDoc = 1, forceRefresh = false } = {}) {
    const cacheKey = `withDoc:${withDoc}`;
    const now = Date.now();

    if (!forceRefresh) {
      const cached = ProfileInfoController.profileInfoCache.get(cacheKey);
      if (
        cached &&
        cached.timestamp &&
        now - cached.timestamp < ProfileInfoController.PROFILE_INFO_CACHE_TTL_MS
      ) {
        return cached.value;
      }

      const inFlight = ProfileInfoController.profileInfoInFlight.get(cacheKey);
      if (inFlight) {
        return await inFlight;
      }
    }

    try {
      const requestPromise = (async () => {
        const response = await postEncryptedJson(API_ROUTES.GET_PROFILE, {
          WITH_DOC: withDoc,
        });
        const data = response?.decryptedData || response?.data;

        if (!data || typeof data !== "object") {
          return { success: false, error: "Invalid response format" };
        }

        if (data.CODE === AppConstants.responseOk && data.DATA) {
          const raw = data.DATA;
          const info = raw.user_information || raw.USER_INFORMATION || raw.userInformation || {};
          const reg = raw.user_regulation || raw.USER_REGULATION || raw.userRegulation || {};

          const result = {
            success: true,
            data: {
              id: raw.ID || raw.id,
              clientId: raw.CLIENT_ID || raw.clientId || "",
              userName: raw.USER_NAME || raw.userName || raw.EMAIL || "",
              nickName: raw.NICK_NAME || raw.nickName || "",
              type: raw.TYPE ?? raw.type,
              internalType: info.INTERNAL_TYPE ?? info.internalType ?? info.internal_type,
              firstName: info.F_NAME || info.FIRST_NAME || info.firstName || "",
              fatherName: info.FATHER_NAME || info.fatherName || "",
              lastName: info.L_NAME || info.LAST_NAME || info.lastName || "",
              motherName: info.MOTHER_NAME || info.motherName || "",
              gender: info.GENDER ?? info.gender,
              profession: info.PROFESSION || info.profession || "",
              marital: info.MARITAL ?? info.marital,
              spouseName: info.SPOUSE_NAME || info.spouseName || "",
              spouseProfession: info.SPOUSE_PROFESSION || info.spouseProfession || "",
              isPep: info.IS_PEP ?? info.isPep ?? false,
              birthDay: info.BIRTH_DAY || info.birthDay || "",
              placeOfBirth: info.PLACE_OF_BIRTH || info.placeOfBirth || "",
              state: info.STATE || info.state || "",
              city: info.CITY || info.city || "",
              zipCode: info.ZIP_CODE || info.zipCode || "",
              address: info.ADDRESS || info.address || "",
              workAddress: info.WORK_ADDRESS || info.workAddress || "",
              country: info.COUNTRY || info.country || "",
              nationality: info.NATIONALITY || info.nationality || "",
              residence: info.RESIDENCE ?? info.residence,
              beneficialRight: info.BENEFICAL_RIGHT ?? info.beneficalRight,
              identificationNumber: info.IDENTIFICATION_NUMBER || info.identificationNumber || "",
              email: info.EMAIL || info.email || raw.EMAIL || raw.USER_NAME || "",
              taxId: reg.TAX_ID || reg.taxId || "",
              netWorth: reg.NET_WORTH ?? reg.netWorth,
              annualIncome: reg.ANNUAL_INCOME ?? reg.annualIncome,
              annualDeposit: reg.ANNUAL_DEPOSIT ?? reg.annualDeposit,
              employmentIndustry: reg.EMPLOYMENT_INDUSTRY ?? reg.employmentIndustry,
              educationLevel: reg.EDUCATION_LEVEL ?? reg.educationLevel,
              sourceOfWealth: reg.SOURCE_OF_WEALTH ?? reg.sourceOfWealth,
              employmentStatus: reg.EMPLOYMENT_STATUS ?? reg.employmentStatus,
              experienceForex: reg.EXPERIENCE_FOREX ?? reg.experienceForex,
              experienceStocks: reg.EXPERIENCE_STOCKS ?? reg.experienceStocks,
              experienceFutures: reg.EXPERIENCE_FUTURES ?? reg.experienceFutures,
              experienceCfd: reg.EXPERIENCE_CFD ?? reg.experienceCfd,
              opsPace: reg.OPS_PACE ?? reg.opsPace ?? null,
              avgTxAmount: reg.AVG_TX_AMOUNT ?? reg.avgTxAmount ?? null,
              expectedAnnualActivity:
                reg.EXPECTED_ANNUAL_ACTIVITY ?? reg.expectedAnnualActivity ?? null,
              raw,
            },
          };

          ProfileInfoController.profileInfoCache.set(cacheKey, {
            timestamp: Date.now(),
            value: result,
          });
          return result;
        }

        if (data.CODE === 401) {
          return { success: false, error: "Authentication failed. Please login again." };
        }

        return {
          success: false,
          error: data.MESSAGE || `Error: CODE ${data.CODE || "Unknown"}`,
        };
      })();

      if (!forceRefresh) {
        ProfileInfoController.profileInfoInFlight.set(cacheKey, requestPromise);
        try {
          return await requestPromise;
        } finally {
          ProfileInfoController.profileInfoInFlight.delete(cacheKey);
        }
      }

      return await requestPromise;
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to load profile information",
      };
    }
  }

  /**
   * Save profile data per step.
   * Matches Flutter _updateUserInformations() which sends step-specific data:
   *   'client'   → USER_ID, IS_CREATE, USER_NICK_NAME, USER_TYPE
   *   'personal' → USER_ID, IS_CREATE, USER_INFORMATION
   *   'address'  → USER_ID, IS_CREATE, USER_INFORMATION
   */
  static async saveProfile({ userId, nickName, userType, userInformation, updateType }) {
    try {
      const body = {
        USER_ID: userId,
        IS_CREATE: 1,
      };

      if (updateType === "client") {
        body.USER_NICK_NAME = nickName;
        body.USER_TYPE = userType;
      } else {
        body.USER_INFORMATION = userInformation;
      }

      const response = await updateProfile(body);
      const result = response?.decryptedData || response?.data;

      if (!result || typeof result !== "object") {
        return { success: false, error: "No valid response from server" };
      }

      const code = Number(result.CODE);
      if (code === AppConstants.responseOk || code === 200) {
        return { success: true };
      }
      return {
        success: false,
        error: result?.MESSAGE || `Server returned CODE ${result?.CODE}`,
      };
    } catch (error) {
      console.error("[ProfileInfoController] saveProfile error:", error);
      return { success: false, error: error.message || "Failed to save profile" };
    }
  }

  /**
   * Save regulation data (Step 4).
   * Uses postEncryptedJson (JSON POST) to match Flutter's DatabaseApi.callServer
   * which uses postData (not multipart) when no images are being uploaded.
   * Must include USER_DOCUMENTS and related fields to match Flutter's full request.
   */
  static async saveRegulation({
    userId,
    userInformation,
    userRegulation,
    userDocuments,
    selectedDocSubTypes,
  }) {
    try {
      const body = {
        USER_ID: userId,
        USER_INFORMATION: userInformation,
        USER_REGULATION: userRegulation,
        USER_DOCUMENTS: userDocuments || [],
        DOCUMENTS_TO_DELETE: {},
        DOCUMENTS_TO_UPDATE: {},
        SELECTED_DOCUMENTS_SUBTYPES: selectedDocSubTypes || {},
      };

      const response = await postEncryptedJson(
        API_ROUTES.UPDATE_PERSONAL_INFORMATION,
        body
      );
      const result = response?.decryptedData || response?.data;

      if (!result || typeof result !== "object") {
        return { success: false, error: "No valid response from server" };
      }

      const code = Number(result.CODE);
      if (code === AppConstants.responseOk || code === 200 || code === AppConstants.catchError) {
        return { success: true };
      }
      return {
        success: false,
        error: result?.MESSAGE || `Server returned CODE ${result?.CODE}`,
      };
    } catch (error) {
      console.error("[ProfileInfoController] saveRegulation error:", error);
      return { success: false, error: error.message || "Failed to save regulation" };
    }
  }

  /**
   * Fetch agreements from API.
   * Mirrors Flutter _loadAgreements() calling GET_AGREEMENTS.
   */
  static async getAgreements() {
    try {
      const response = await postEncryptedJson(API_ROUTES.GET_AGREEMENTS, {});
      const data = response?.decryptedData || response?.data;

      if (data?.CODE === AppConstants.responseOk && Array.isArray(data.DATA)) {
        return { success: true, data: data.DATA };
      }
      return { success: true, data: [] };
    } catch (error) {
      console.error("[ProfileInfoController] getAgreements error:", error);
      return { success: false, data: [], error: error.message };
    }
  }

  /**
   * Submit documents and agreement signatures (Step 5).
   * Mirrors Flutter _insertUserDocuments() calling CREATE_USER_DOCUMENTS.
   */
  static async submitDocuments({ email, identityDocType, addressDocType, imageFiles, agreementSignatures }) {
    try {
      const hasSignatureVideo = !!(imageFiles && imageFiles.SIGNATURE_VIDEO);
      const userDocuments = {
        SELFIE_IMAGE: UserDocumentSubType.SELFIE,
        FRONT_ID_IMAGE: identityDocType ?? null,
        BACK_ID_IMAGE: identityDocType ?? "",
        ADDRESS_IMAGE: addressDocType ?? null,
        SHAREHOLDER_DOCUMENT_TYPE: null,
        DIRECTOR_DOCUMENT_TYPE: null,
        BENIFICAL_DOCUMENT_TYPE: UserDocumentSubType.ID_CARD,
        SIGNATURE_DOCUMENT_TYPE: UserDocumentSubType.SIGNATURE,
        ...(hasSignatureVideo ? { SIGNATURE_VIDEO_TYPE: UserDocumentSubType.SIGNATURE_VIDEO } : {}),
        SIGNATURE_CHECK_TYPE: UserDocumentSubType.CHECK_SIGNATURE,
      };

      const data = {
        EMAIL: email,
        IS_CREATE: 1,
        USER_DOCUMENTS: userDocuments,
      };

      // Add agreement ID mappings (AGREEMENT_{ID}_ID = id)
      if (agreementSignatures) {
        for (const [agreementId] of Object.entries(agreementSignatures)) {
          data[`AGREEMENT_${agreementId}_ID`] = String(agreementId);
        }
      }

      // Build imageFiles map: document files + agreement signature images
      const allFiles = { ...imageFiles };
      if (agreementSignatures) {
        for (const [agreementId, file] of Object.entries(agreementSignatures)) {
          if (file) {
            allFiles[`AGREEMENT_${agreementId}_SIGNATURE`] = file;
          }
        }
        const firstSignature = Object.values(agreementSignatures).find(Boolean);
        if (firstSignature && !allFiles.SIGNATURE_AGREEMENT_IMAGE) {
          allFiles.SIGNATURE_AGREEMENT_IMAGE = firstSignature;
        }
      }

      const response = await postEncryptedFormData(
        API_ROUTES.CREATE_USER_DOCUMENTS_MT5_ACCOUNT,
        data,
        allFiles
      );
      const result = response?.decryptedData || response?.data;

      if (!result || typeof result !== "object") {
        return { success: false, error: "No valid response from server" };
      }

      const code = Number(result.CODE);
      if (code === AppConstants.responseOk || code === 200) {
        return { success: true };
      }
      if (code === AppConstants.responseFailed) {
        return { success: false, error: "Duplicate submission attempt" };
      }
      return {
        success: false,
        error: result?.MESSAGE || `Server returned CODE ${result?.CODE}`,
      };
    } catch (error) {
      console.error("[ProfileInfoController] submitDocuments error:", error);
      return { success: false, error: error.message || "Failed to submit documents" };
    }
  }

  /**
   * Update user profile (edit mode) — mirrors Flutter _updateUserInformations().
   * Sends user info + documents + images to UPDATE_PERSONAL_PROFILE via FormData.
   */
  static async updateUserProfile({
    userId,
    userInformation,
    nickName,
    userType,
    userDocuments: existingDocs,
    selectedDocSubTypes,
    imageFiles,
    imagesToUpdate,
    imagesToDelete,
  }) {
    try {
      const data = {
        USER_ID: userId,
        USER_DOCUMENTS: existingDocs || [],
        DOCUMENTS_TO_DELETE: imagesToDelete || {},
        DOCUMENTS_TO_UPDATE: imagesToUpdate || {},
        USER_INFORMATION: userInformation,
        USER_NICK_NAME: nickName,
        USER_TYPE: userType,
        SELECTED_DOCUMENTS_SUBTYPES: selectedDocSubTypes || {},
      };

      const response = await postEncryptedFormData(
        API_ROUTES.UPDATE_PERSONAL_PROFILE,
        data,
        imageFiles || {}
      );
      const result = response?.decryptedData || response?.data;

      if (!result || typeof result !== "object") {
        return { success: false, error: "No valid response from server" };
      }

      const code = Number(result.CODE);
      if (code === AppConstants.responseOk || code === 200 || code === AppConstants.catchError) {
        return { success: true };
      }
      return {
        success: false,
        error: result?.MESSAGE || `Server returned CODE ${result?.CODE}`,
      };
    } catch (error) {
      console.error("[ProfileInfoController] updateUserProfile error:", error);
      return { success: false, error: error.message || "Failed to update profile" };
    }
  }

  /**
   * Helper to extract document image URLs from raw profile data.
   */
  static extractDocumentUrls(rawData) {
    const rawDocs =
      rawData?.user_document ||
      rawData?.user_documents ||
      rawData?.USER_DOCUMENTS ||
      rawData?.USER_DOCUMENT ||
      rawData?.userDocuments ||
      [];
    const docs = Array.isArray(rawDocs) ? rawDocs : rawDocs ? [rawDocs] : [];
    const result = {};

    const unwrapEnum = (value) => {
      if (value == null || typeof value !== "object") return value;
      return value.value ?? value.VALUE ?? value.name ?? value.NAME ?? value;
    };

    const numEq = (a, b) => {
      a = unwrapEnum(a);
      b = unwrapEnum(b);
      if (a == null || b == null) return a == null && b == null;
      const aNum = Number(a);
      const bNum = Number(b);
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum) && String(a).trim() !== "" && String(b).trim() !== "") {
        return aNum === bNum;
      }
      return String(a).toUpperCase() === String(b).toUpperCase();
    };

    const asFileList = (files) => {
      if (Array.isArray(files)) return files;
      if (files && typeof files === "object") return [files];
      return [];
    };

    const findDocFile = (docType, docSubType, fileName) => {
      for (const doc of docs) {
        const dt = doc.DOC_TYPE ?? doc.docType ?? doc.doc_type;
        const dst = doc.DOC_SUB_TYPE ?? doc.docSubType ?? doc.doc_sub_type;
        if (numEq(dt, docType) && (docSubType == null || numEq(dst, docSubType))) {
          const files = asFileList(
            doc.user_document_file ||
              doc.user_document_files ||
              doc.USER_DOCUMENT_FILE ||
              doc.USER_DOCUMENT_FILES ||
              doc.userDocumentFiles ||
              doc.userDocumentFile
          );
          for (const f of files) {
            const isDeleted = f.IS_DELETED ?? f.isDeleted ?? f.is_deleted;
            if (isDeleted === true || isDeleted === 1 || isDeleted === "1") continue;
            const fn = f.FILE_NAME ?? f.fileName ?? f.file_name;
            if (fileName != null && String(fn) !== String(fileName)) continue;
            const path = f.FILE_PATH ?? f.filePath ?? f.file_path;
            if (path) return path;
          }
        }
      }
      return null;
    };

    const findWithFallback = (docType, docSubType, fileName) =>
      findDocFile(docType, docSubType, fileName) || findDocFile(docType, docSubType, null);

    result.frontId = buildDocImageUrl(findWithFallback(UserDocumentType.PROOF_OF_IDENTITY, null, DocumentFileName.FRONT_IMAGE));
    result.backId = buildDocImageUrl(findDocFile(UserDocumentType.PROOF_OF_IDENTITY, null, DocumentFileName.BACK_IMAGE));
    result.selfie = buildDocImageUrl(findWithFallback(UserDocumentType.SELFIE, UserDocumentSubType.SELFIE, DocumentFileName.SELFIE));
    result.profile = buildDocImageUrl(
      findWithFallback(UserDocumentType.PROFILE, UserDocumentSubType.PROFILE, DocumentFileName.PROFILE) ||
        findDocFile(UserDocumentType.PROFILE, null, DocumentFileName.PROFILE) ||
        findDocFile(UserDocumentType.PROFILE, null, null)
    );
    result.address = buildDocImageUrl(findDocFile(UserDocumentType.PROOF_OF_ADDRESS, null, null));
    result.signature = buildDocImageUrl(findWithFallback(UserDocumentType.PROOF_OF_SIGNATURE, UserDocumentSubType.SIGNATURE, DocumentFileName.FRONT_IMAGE));

    result.identityDocSubType = null;
    result.addressDocSubType = null;
    for (const doc of docs) {
      const dt = doc.DOC_TYPE ?? doc.docType ?? doc.doc_type;
      const dst = doc.DOC_SUB_TYPE ?? doc.docSubType ?? doc.doc_sub_type;
      const dtNum = Number(dt);
      const dstNum = Number(dst);
      if (dtNum === UserDocumentType.PROOF_OF_IDENTITY && dstNum !== UserDocumentSubType.SELFIE && dstNum !== UserDocumentSubType.PROFILE) {
        result.identityDocSubType = dstNum;
      }
      if (dtNum === UserDocumentType.PROOF_OF_ADDRESS) {
        result.addressDocSubType = dstNum;
      }
    }

    result.rawDocuments = docs;
    return result;
  }

  /**
   * Mark profile as completed.
   */
  static async completeProfile() {
    try {
      const response = await updateProfileStatus();
      const result = response?.decryptedData || response?.data;

      if (!result || typeof result !== "object") {
        return { success: false, error: "No valid response from server" };
      }

      const code = Number(result.CODE);
      if (code === AppConstants.responseOk || code === 200) {
        return { success: true };
      }
      return {
        success: false,
        error: result?.MESSAGE || `Server returned CODE ${result?.CODE}`,
      };
    } catch (error) {
      console.error("[ProfileInfoController] completeProfile error:", error);
      return { success: false, error: error.message || "Failed to complete profile" };
    }
  }

  static getDisplayName(user) {
    if (!user || typeof user !== "object") return "";
    return (
      user.user_information?.FULL_NAME ||
      user.userInformation?.FULL_NAME ||
      user.NICK_NAME ||
      user.USER_NAME ||
      user.user_information?.EMAIL ||
      user.userInformation?.EMAIL ||
      ""
    );
  }
}

export default ProfileInfoController;
